import React, { Component } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SimpleReactValidator from 'simple-react-validator';
import moment from 'moment-timezone';
import service from '../services';
import TextField from '../component/TextField';
import DropdownMaster from '../component/DropdownMaster';
import Loader from '../component/loader';
import CatEditIcon from '../icons/catediticon';
import DraggerIcon from '../icons/draggericon';
import FpgPlusIcon from '../icons/fpgplusicon';
import FpgMinusIcon from '../icons/fpgminusicon';

// ── Socket event names ────────────────────────────────────────────────────────
const EVENTS = {
    GET:          'configuration|rsvpForm|get',
    EDIT:         'configuration|rsvpForm|edit',
    CHANGE_ORDER: 'configuration|rsvpForm|changeOrder',
};

const FIELD_TYPES = [
    { _id: 'text',     name: 'Text' },
    { _id: 'textarea', name: 'Textarea' },
    { _id: 'dropdown', name: 'Dropdown' },
    { _id: 'checkbox', name: 'Checkbox' },
    { _id: 'radio',    name: 'Radio' },
    { _id: 'email',    name: 'Email' },
    { _id: 'number',   name: 'Number' },
    { _id: 'tc',       name: 'T&C' },
];

const OPTION_FIELD_TYPES = ['radio', 'checkbox', 'dropdown'];

const EMPTY_FIELD = {
    key:        '',
    fieldName:  '',
    fieldType:  '',
    isRequired: false,
    isVisible:  true,
    options:    [],
};

// ── Data mappers ──────────────────────────────────────────────────────────────

// API → internal: map the backend field shape to the component's internal shape.
// API options are plain strings; internally we store { id, value } for stable keys.
const fromApiField = (f) => ({
    key:        f.key,
    fieldName:  f.field,
    fieldType:  f.fieldType,
    isRequired: f.isRequired,
    isVisible:  !f.hideField,
    options:    (f.options || []).map((v) => ({
        id:    String(v),
        value: String(v),
    })),
});

// Internal → API: map the component's internal shape to what the backend expects.
const toApiField = (f) => ({
    field:     f.fieldName.trim(),
    key:       f.key,
    fieldType: f.fieldType,
    isRequired: f.isRequired,
    hideField:  !f.isVisible,
    options:    (f.options || []).map((o) => o.value).filter(Boolean),
});

// Generate a snake_case key from a field name (e.g. "First Name" → "first_name").
const generateKey = (fieldName) =>
    fieldName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

let sub;

class RsvpForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaderFlag:    false,
            fields:        [],
            rsvpformId:    null,   // document _id — required by changeOrder
            lastUpdatedAt: null,
            lastUpdatedBy: null,

            // Add/Edit modal
            addFieldModal:    false,
            isEditMode:       false,
            isEdited:         false,
            currentField:     { ...EMPTY_FIELD },
            editOriginalKey:  null,   // original key of the field being edited (for lookup in fields[])

            // Nested options manager modal
            optionsModal: false,

            // Error popup
            errorMsg:     false,
            errorMsgShow: '',
        };
        this.validator = new SimpleReactValidator({ autoForceUpdate: this });
        this.apiserve  = new service();
    }

    componentDidMount = () => {
        this.subscribeToEvents();
        this.fetchFields();
    };

    componentWillUnmount = () => {
        sub && sub.unsubscribe();
    };

    fetchFields = () => {
        this.setState({ loaderFlag: true });
        this.apiserve.sendevent(EVENTS.GET, {});
    };

    subscribeToEvents = () => {
        sub = this.apiserve.getevent().subscribe((response) => {
            switch (response.event) {

                case EVENTS.GET: {
                    // 404 means no configuration exists yet — show empty state, not an error.
                    if (response.statusCode === 404) {
                        this.setState({ loaderFlag: false, fields: [] });
                        break;
                    }
                    if (response.error) {
                        this.setState({
                            loaderFlag:   false,
                            errorMsg:     true,
                            errorMsgShow: (response.data || {}).error || 'Failed to load RSVP fields.',
                        });
                        break;
                    }
                    const data = response.data || {};
                    this.setState({
                        loaderFlag:    false,
                        rsvpformId:    data._id || null,
                        fields:        (data.rsvpformfields || []).map(fromApiField),
                        lastUpdatedAt: data.updatedAt || null,
                        lastUpdatedBy: data.updatedBy || null,
                    });
                    break;
                }

                case EVENTS.EDIT: {
                    if (response.error) {
                        this.setState({
                            loaderFlag:   false,
                            errorMsg:     true,
                            errorMsgShow: (response.data || {}).error || 'Failed to save field.',
                        });
                        break;
                    }
                    const msg = this.state.isEditMode
                        ? 'Field updated successfully.'
                        : 'Field added successfully.';
                    const data = response.data || {};
                    this.setState(
                        {
                            addFieldModal: false,
                            optionsModal:  false,
                            loaderFlag:    false,
                            rsvpformId:    data._id || this.state.rsvpformId,
                            fields:        (data.rsvpformfields || []).map(fromApiField),
                            lastUpdatedAt: data.updatedAt || null,
                            lastUpdatedBy: data.updatedBy || null,
                        },
                        () => this.apiserve.success(msg)
                    );
                    break;
                }

                case EVENTS.CHANGE_ORDER: {
                    if (response.error) {
                        // Reorder failed — re-fetch to restore server order.
                        this.apiserve.error('Failed to save new field order.');
                        this.fetchFields();
                        break;
                    }
                    const data = response.data || {};
                    this.setState({
                        loaderFlag:    false,
                        fields:        (data.rsvpformfields || []).map(fromApiField),
                        lastUpdatedAt: data.updatedAt || null,
                        lastUpdatedBy: data.updatedBy || null,
                    });
                    break;
                }

                default:
                    break;
            }
        });
    };

    // ── Modal open / close ────────────────────────────────────────────────────

    openAddModal = () => {
        this.validator.hideMessages();
        this.setState({
            addFieldModal: true,
            isEditMode:    false,
            isEdited:      true,
            currentField:  { ...EMPTY_FIELD },
        });
    };

    openEditModal = (field) => {
        this.validator.hideMessages();
        this.setState({
            addFieldModal:   true,
            isEditMode:      true,
            isEdited:        false,
            editOriginalKey: field.key,
            currentField: {
                key:        generateKey(field.fieldName),
                fieldName:  field.fieldName,
                fieldType:  field.fieldType,
                isRequired: field.isRequired,
                isVisible:  field.isVisible,
                options:    [...(field.options || [])],
            },
        });
    };

    closeFieldModal = () => {
        this.validator.hideMessages();
        this.setState({
            addFieldModal:   false,
            isEditMode:      false,
            isEdited:        false,
            currentField:    { ...EMPTY_FIELD },
            optionsModal:    false,
            editOriginalKey: null,
        });
    };

    // ── Options modal ─────────────────────────────────────────────────────────

    openOptionsModal = () => {
        this.setState({ optionsModal: true });
    };

    closeOptionsModal = () => {
        this.setState({ optionsModal: false });
    };

    addOption = () => {
        const options = [
            ...this.state.currentField.options,
            { id: Date.now().toString(), value: '' },
        ];
        this.setState({
            currentField: { ...this.state.currentField, options },
            isEdited: true,
        });
    };

    updateOption = (index, value) => {
        const options = [...this.state.currentField.options];
        options[index] = { ...options[index], value };
        this.setState({
            currentField: { ...this.state.currentField, options },
            isEdited: true,
        });
    };

    removeOption = (index) => {
        const options = this.state.currentField.options.filter((_, i) => i !== index);
        this.setState({
            currentField: { ...this.state.currentField, options },
            isEdited: true,
        });
    };

    // ── Field form handlers ───────────────────────────────────────────────────

    onFieldNameChange = (e) => {
        const fieldName = e.target.value.trimStart();
        this.setState({
            currentField: {
                ...this.state.currentField,
                fieldName,
                key: generateKey(fieldName),
            },
            isEdited: true,
        });
    };

    onFieldTypeChange = (e) => {
        const fieldType = e.target.value;
        const options = OPTION_FIELD_TYPES.includes(fieldType)
            ? this.state.currentField.options
            : [];
        this.setState({
            currentField: { ...this.state.currentField, fieldType, options },
            isEdited: true,
        });
    };

    onToggleChange = (name) => {
        this.setState({
            currentField: {
                ...this.state.currentField,
                [name]: !this.state.currentField[name],
            },
            isEdited: true,
        });
    };

    // ── Save (add or edit — single API call) ──────────────────────────────────

    submitField = (e) => {
        e.preventDefault();
        if (!this.validator.allValid()) {
            this.validator.showMessages();
            this.forceUpdate();
            return;
        }
        const { currentField, isEditMode, fields, editOriginalKey } = this.state;

        // Build full merged list: edit updates in place, add appends to end
        const rsvpformfields = isEditMode
            ? fields.map((f) => f.key === editOriginalKey ? toApiField(currentField) : toApiField(f))
            : [...fields.map(toApiField), toApiField(currentField)];

        this.setState({ loaderFlag: true });
        this.apiserve.sendevent(EVENTS.EDIT, { rsvpformfields, rsvpformId:this.state.rsvpformId });
    };

    // ── Drag and drop ─────────────────────────────────────────────────────────

    onDragEnd = (result) => {
        if (!result.destination) return;
        if (result.destination.index === result.source.index) return;

        const fields = [...this.state.fields];
        const [moved] = fields.splice(result.source.index, 1);
        fields.splice(result.destination.index, 0, moved);

        // Optimistic local update, then persist order to backend.
        this.setState({ fields, loaderFlag: true });
        this.apiserve.sendevent(EVENTS.CHANGE_ORDER, {
            rsvpformId:      this.state.rsvpformId,
            rsvpformfields:  fields.map(toApiField),
        });
    };

    // ── Error popup dismiss ───────────────────────────────────────────────────

    dismissError = () => {
        this.setState({ errorMsg: false });
    };

    // ── Render ────────────────────────────────────────────────────────────────

    render() {
        const {
            fields, loaderFlag,
            addFieldModal, isEditMode, isEdited, currentField,
            optionsModal, lastUpdatedAt, lastUpdatedBy,
            errorMsg, errorMsgShow,
        } = this.state;

        const showOptions   = OPTION_FIELD_TYPES.includes(currentField.fieldType);
        const isSaveDisabled = isEditMode && !isEdited;

        return (
            <>
                <div className="cat_outer_content">
                    <div className="category_add_edit">

                        {/* ── Last Updated — object level ── */}
                        {lastUpdatedAt && (
                            <div className="back_date d_flex d_flex_align_center d_flex_justify_spacebt">
                                <p></p>
                                <p className="update_date">
                                    Last Update:{' '}
                                    {moment(lastUpdatedAt).tz('US/Mountain').format('h')}:
                                    {moment(lastUpdatedAt).tz('US/Mountain').format('mm')}{' '}
                                    {moment(lastUpdatedAt).tz('US/Mountain').format('A')}{' '}
                                    MT- {moment.tz(lastUpdatedAt, 'US/Mountain').format('MM/DD/YYYY')}
                                    {lastUpdatedBy && (
                                        <>
                                            {' '}Last Updated By:{' '}
                                            {lastUpdatedBy.fname} {lastUpdatedBy.lname}
                                        </>
                                    )}
                                </p>
                            </div>
                        )}

                        {/* ── Field list ── */}
                        {fields.length === 0 ? (
                            <div className="no_records text_center">
                                <p>No fields added yet. Click "+" to add a field.</p>
                            </div>
                        ) : (
                            <div className="list_wrap striped_droppable_table rsvp_field_table">
                                {/* Column header row */}
                                <div className="rsvp_table_header d_flex d_flex_align_center">
                                    <span className="col_drag"></span>
                                    <span className="col_name">Field Name</span>
                                    <span className="col_type">Field Type</span>
                                    <span className="col_req">Required</span>
                                    <span className="col_vis">Visible</span>
                                    <span className="col_action">Action</span>
                                </div>

                                <DragDropContext onDragEnd={this.onDragEnd}>
                                    <Droppable droppableId="rsvp-fields">
                                        {(provided) => (
                                            <ul
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {fields.map((field, index) => (
                                                    <Draggable
                                                        key={field.key}
                                                        draggableId={field.key}
                                                        index={index}
                                                    >
                                                        {(provided) => (
                                                            <li
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className="d_flex d_flex_align_center"
                                                            >
                                                                <span
                                                                    className="col_drag cat_drag d_flex"
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <DraggerIcon />
                                                                </span>
                                                                <span className="col_name cat_name">
                                                                    {field.fieldName}
                                                                </span>
                                                                <span className="col_type">
                                                                    {field.fieldType}
                                                                </span>
                                                                <span className="col_req">
                                                                    {field.isRequired ? 'Yes' : 'No'}
                                                                </span>
                                                                <span className="col_vis">
                                                                    {field.isVisible ? 'Yes' : 'No'}
                                                                </span>
                                                                <div className="col_action action d_flex d_flex_align_center">
                                                                    <span
                                                                        className="cat_edit pointer"
                                                                        onClick={() => this.openEditModal(field)}
                                                                    >
                                                                        <CatEditIcon />
                                                                    </span>
                                                                </div>
                                                            </li>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </ul>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Add / Edit Field Modal ── */}
                <div className={`user_popup ${addFieldModal ? 'active' : ''}`}>
                    <span className="popup_overlay" onClick={this.closeFieldModal}></span>
                    <div className="cat_modal_inner" style={{maxWidth:'510px'}}>
                        <p className="cat_title text_center">
                            {isEditMode ? 'Edit Field' : 'Add Field'}
                        </p>
                        <form>
                            {/* Field Name */}
                            <div className="field_row horizontal_field d_flex">
                                <TextField
                                    type="text"
                                    label="Field Name"
                                    name="fieldName"
                                    require
                                    value={currentField.fieldName}
                                    onChange={this.onFieldNameChange}
                                    fpgerror={this.validator.message(
                                        'Field Name',
                                        currentField.fieldName,
                                        'required'
                                    )}
                                />
                            </div>

                            {/* Field Type */}
                            <div className="field_row horizontal_field d_flex">
                                <DropdownMaster
                                    label="Field Type"
                                    name="fieldType"
                                    require
                                    options={FIELD_TYPES}
                                    value={currentField.fieldType}
                                    onChange={this.onFieldTypeChange}
                                    fpgerror={this.validator.message(
                                        'Field Type',
                                        currentField.fieldType,
                                        'required'
                                    )}
                                />
                            </div>

                            {/* Options — visible only for radio, checkbox, dropdown */}
                            {showOptions && (
                                <div className="field_row horizontal_field d_flex d_flex_align_center">
                                    <div className="field_row_inner">
                                        <label>Options</label>
                                        <div className="field_input">
                                            <span
                                                className="view_options_link pointer"
                                                onClick={this.openOptionsModal}
                                            >
                                                View Options
                                                {currentField.options.length > 0 && (
                                                    <span className="options_count">
                                                        {' '}({currentField.options.length})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Is Required */}
                            <div className="field_row horizontal_field">
                                <div className="field_row_inner">
                                    <label className="null">Is Required</label>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={currentField.isRequired}
                                            onChange={() => this.onToggleChange('isRequired')}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>
                            </div>

                            {/* Is Visible */}
                            <div className="field_row horizontal_field">
                                <div className="field_row_inner">
                                    <label className="null">Is Visible</label>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={currentField.isVisible}
                                            onChange={() => this.onToggleChange('isVisible')}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>
                            </div>

                            {/* Modal buttons */}
                            <div className="cat_btns text_center">
                                <button type="button" onClick={this.closeFieldModal}>
                                    CANCEL
                                </button>
                                <button
                                    type="button"
                                    className={isSaveDisabled ? 'disabled' : 'active'}
                                    disabled={isSaveDisabled}
                                    onClick={this.submitField}
                                >
                                    SAVE
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* ── Options Manager Modal (nested inside field modal flow) ── */}
                <div className={`user_popup rsvp_options_popup ${optionsModal ? 'active' : ''}`}>
                    <span className="popup_overlay" onClick={this.closeOptionsModal}></span>
                    <div className="cat_modal_inner rsvp_options_inner">
                        <p className="cat_title text_center">Manage Options</p>

                        <div className="rsvp_options_list">
                            {currentField.options.length === 0 && (
                                <p className="text_center rsvp_no_options">
                                    No options yet. Click "Add Option" to add one.
                                </p>
                            )}
                            {currentField.options.map((opt, index) => (
                                <div
                                    key={opt.id || index}
                                    className="rsvp_option_row d_flex d_flex_align_center"
                                >
                                    <input
                                        type="text"
                                        className="rsvp_option_input"
                                        placeholder={`Option ${index + 1}`}
                                        value={opt.value}
                                        onChange={(e) => this.updateOption(index, e.target.value)}
                                    />
                                    <span
                                        className="remove_cat pointer"
                                        onClick={() => this.removeOption(index)}
                                    >
                                        <FpgMinusIcon width="16" height="16" color="#4F4F4F" />
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div
                            className="rsvp_add_option d_flex d_flex_align_center pointer"
                            onClick={this.addOption}
                        >
                            <FpgPlusIcon width="14" height="14" color="#ffffff" stroke="#4F4F4F" />
                            <span>Add Option</span>
                        </div>

                        <div className="cat_btns text_center">
                            <button
                                type="button"
                                className="active"
                                onClick={this.closeOptionsModal}
                            >
                                DONE
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Error popup ── */}
                <div className={`user_popup ${errorMsg ? 'active' : ''}`}>
                    <span className="popup_overlay"></span>
                    <div className="sundance_popup_detail box_shadow approve_success_popup bg_white">
                        <div className="approve_popup_header text_center relative">
                            <p>{errorMsgShow}</p>
                        </div>
                        <div className="content">
                            <div className="field_row text_center">
                                <button className="small_btn active" onClick={this.dismissError}>
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {loaderFlag ? <Loader /> : ''}
            </>
        );
    }
}

export default RsvpForm;
